export type ParameterType =
  | "string"
  | "textarea"
  | "integer"
  | "number"
  | "boolean"
  | "select"
  | "chain"
  | "date"
  | "datetime"
  | "file"
  | "json"
  | "array";

export type ParameterOption = {
  label: string;
  value: string;
};

export type ParameterDefinition = {
  name: string;
  label: string;
  type: ParameterType;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string;
  options?: ParameterOption[];
  accept?: string;
  maxBytes?: number;
  format?: "email" | "uri";
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  items?: ParameterDefinition;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
};

export type ResponseFieldDefinition = {
  path: string;
  label: string;
  format: "text" | "number" | "boolean" | "datetime" | "link" | "json";
};

export type ResponseDisplayDefinition = {
  mode: "JSON" | "KEY_VALUE" | "LIST" | "TABLE" | "TEXT";
  root?: string;
  fields?: ResponseFieldDefinition[];
  summaryRoot?: string;
  summaryFields?: ResponseFieldDefinition[];
};

const PARAMETER_TYPES = new Set<ParameterType>([
  "string", "textarea", "integer", "number", "boolean", "select", "chain",
  "date", "datetime", "file", "json", "array",
]);
const ARRAY_ITEM_TYPES = new Set<ParameterType>([
  "string", "integer", "number", "boolean", "select", "chain", "date", "datetime",
]);
const RESPONSE_MODES = new Set<ResponseDisplayDefinition["mode"]>([
  "JSON", "KEY_VALUE", "LIST", "TABLE", "TEXT",
]);
const RESPONSE_FORMATS = new Set<ResponseFieldDefinition["format"]>([
  "text", "number", "boolean", "datetime", "link", "json",
]);

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseJSONObject(raw: string, label: string): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(`${label} 不是合法 JSON`);
  }
  if (!isRecord(parsed)) throw new Error(`${label} 必须是 JSON 对象`);
  return parsed;
}

export function parseParameterDefinitions(raw: string): ParameterDefinition[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw || "[]");
  } catch {
    throw new Error("Request Schema 不是合法 JSON");
  }
  if (!Array.isArray(parsed)) throw new Error("Request Schema 必须是 JSON 数组");
  return parsed as ParameterDefinition[];
}

export function parseResponseDefinition(raw: string): ResponseDisplayDefinition {
  const parsed = parseJSONObject(raw || "{}", "Response Schema") as Partial<ResponseDisplayDefinition>;
  const mode = String(parsed.mode || "JSON").toUpperCase() as ResponseDisplayDefinition["mode"];
  const fields = (value: unknown): ResponseFieldDefinition[] => Array.isArray(value)
    ? value.map(item => {
      const field = isRecord(item) ? item : {};
      const path = typeof field.path === "string" ? field.path : "";
      return {
        path,
        label: typeof field.label === "string" && field.label.trim() ? field.label : path,
        format: String(field.format || "text").toLowerCase() as ResponseFieldDefinition["format"],
      };
    })
    : [];
  return {
    mode,
    root: typeof parsed.root === "string" ? parsed.root : undefined,
    fields: fields(parsed.fields),
    summaryRoot: typeof parsed.summaryRoot === "string" ? parsed.summaryRoot : undefined,
    summaryFields: fields(parsed.summaryFields),
  };
}

function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return words || key;
}

function stringFormat(value: string): ParameterDefinition["format"] {
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "email";
  if (/^https?:\/\//i.test(value)) return "uri";
  return undefined;
}

function scalarParameter(name: string, value: unknown, arrayItem = false): ParameterDefinition {
  const base: ParameterDefinition = {
    name,
    label: humanizeKey(name),
    type: "string",
    required: !arrayItem,
  };
  if (typeof value === "boolean") {
    return { ...base, type: "boolean", defaultValue: String(value) };
  }
  if (typeof value === "number") {
    return { ...base, type: Number.isInteger(value) ? "integer" : "number", defaultValue: String(value) };
  }
  if (typeof value === "string") {
    const format = stringFormat(value);
    return { ...base, type: "string", defaultValue: value, format };
  }
  return base;
}

function canBeArrayItem(value: unknown): boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

export function inferParametersFromExample(example: Record<string, unknown>): ParameterDefinition[] {
  return Object.entries(example).map(([name, value]) => {
    if (Array.isArray(value)) {
      if (value.every(canBeArrayItem)) {
        const sample = value.find(item => item !== null) ?? "";
        const items = scalarParameter(`${name}Item`, sample, true);
        items.name = "item";
        items.label = "每项";
        items.required = false;
        delete items.defaultValue;
        return {
          name,
          label: humanizeKey(name),
          type: "array",
          required: true,
          defaultValue: JSON.stringify(value),
          items,
          maxItems: Math.max(20, value.length),
        };
      }
      return {
        name,
        label: humanizeKey(name),
        type: "json",
        required: true,
        defaultValue: JSON.stringify(value),
      };
    }
    if (isRecord(value) || value === null) {
      return {
        name,
        label: humanizeKey(name),
        type: "json",
        required: true,
        defaultValue: JSON.stringify(value),
      };
    }
    return scalarParameter(name, value);
  });
}

function typedDefault(definition: ParameterDefinition): unknown {
  const raw = definition.defaultValue;
  if (raw === undefined || raw === "" || definition.type === "file") return undefined;
  if (definition.type === "number") {
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  }
  if (definition.type === "integer") {
    const value = Number.parseInt(raw, 10);
    return Number.isFinite(value) ? value : undefined;
  }
  if (definition.type === "boolean") return raw === "true";
  if (definition.type === "json" || definition.type === "array") {
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  return raw;
}

export function templateFromParameters(
  definitions: ParameterDefinition[],
  fallback: Record<string, unknown> = {},
): Record<string, unknown> {
  if (definitions.length === 0) return fallback;
  const values: Record<string, unknown> = {};
  for (const definition of definitions) {
    const value = typedDefault(definition);
    if (value !== undefined) values[definition.name] = value;
    else if (Object.hasOwn(fallback, definition.name)) values[definition.name] = fallback[definition.name];
  }
  return values;
}

function responseFormat(value: unknown): ResponseFieldDefinition["format"] {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "string") {
    if (/^https?:\/\//i.test(value)) return "link";
    if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(Date.parse(value))) return "datetime";
    return "text";
  }
  return "json";
}

function responseFields(
  value: unknown,
  prefix = "",
  depth = 0,
  limit = 12,
): ResponseFieldDefinition[] {
  if (!isRecord(value) || depth > 2) return [];
  const fields: ResponseFieldDefinition[] = [];
  for (const [key, child] of Object.entries(value)) {
    if (fields.length >= limit) break;
    const path = prefix ? `${prefix}.${key}` : key;
    if (isRecord(child) && depth < 2) {
      const nested = responseFields(child, path, depth + 1, limit - fields.length);
      if (nested.length > 0) fields.push(...nested);
      else fields.push({ path, label: humanizeKey(key), format: "json" });
      continue;
    }
    fields.push({ path, label: humanizeKey(key), format: responseFormat(child) });
  }
  return fields;
}

type ArrayCandidate = {
  root: string;
  parentRoot: string;
  parent: Record<string, unknown>;
  key: string;
  rows: Record<string, unknown>[];
};

function findObjectArray(
  value: Record<string, unknown>,
  root = "",
  depth = 0,
): ArrayCandidate | undefined {
  for (const [key, child] of Object.entries(value)) {
    if (Array.isArray(child) && child.length > 0 && child.every(isRecord)) {
      return {
        root: root ? `${root}.${key}` : key,
        parentRoot: root,
        parent: value,
        key,
        rows: child,
      };
    }
  }
  if (depth >= 2) return undefined;
  for (const [key, child] of Object.entries(value)) {
    if (!isRecord(child)) continue;
    const childRoot = root ? `${root}.${key}` : key;
    const candidate = findObjectArray(child, childRoot, depth + 1);
    if (candidate) return candidate;
  }
  return undefined;
}

export function inferResponseDefinition(example: unknown): ResponseDisplayDefinition {
  if (Array.isArray(example)) {
    if (example.length > 0 && example.every(isRecord)) {
      const fields = responseFields(example[0]);
      return fields.length > 0 ? { mode: "TABLE", root: "", fields } : { mode: "JSON" };
    }
    return { mode: "JSON" };
  }
  if (!isRecord(example)) return { mode: "TEXT", root: "" };
  const candidate = findObjectArray(example);
  if (candidate) {
    const fields = responseFields(candidate.rows[0]);
    if (fields.length === 0) return { mode: "JSON" };
    const summarySource = Object.fromEntries(
      Object.entries(candidate.parent).filter(([key, value]) => key !== candidate.key && !Array.isArray(value)),
    );
    const summaryFields = responseFields(summarySource);
    if (summaryFields.length > 0) {
      return {
        mode: "LIST",
        root: candidate.root,
        fields,
        summaryRoot: candidate.parentRoot,
        summaryFields,
      };
    }
    return { mode: "TABLE", root: candidate.root, fields };
  }
  const fields = responseFields(example);
  return fields.length > 0 ? { mode: "KEY_VALUE", root: "", fields } : { mode: "JSON" };
}

function validateScalar(definition: ParameterDefinition, label: string, arrayItem = false): string[] {
  const errors: string[] = [];
  if (!PARAMETER_TYPES.has(definition.type)) errors.push(`${label} 的类型不受支持`);
  if (arrayItem && !ARRAY_ITEM_TYPES.has(definition.type)) errors.push(`${label} 不支持嵌套数组或 JSON`);
  if ((definition.type === "select" || definition.type === "chain") && !definition.options?.length) {
    errors.push(`${label} 至少需要一个选项`);
  }
  if (definition.format && definition.type !== "string" && definition.type !== "textarea") {
    errors.push(`${label} 只有文本类型可以设置格式`);
  }
  if ((definition.minLength || definition.maxLength || definition.pattern) &&
      definition.type !== "string" && definition.type !== "textarea") {
    errors.push(`${label} 只有文本类型可以设置长度或正则`);
  }
  if ((definition.minLength || 0) < 0 || (definition.maxLength || 0) < 0) {
    errors.push(`${label} 的长度不能为负数`);
  }
  if (definition.maxLength && (definition.minLength || 0) > definition.maxLength) {
    errors.push(`${label} 的最小长度不能超过最大长度`);
  }
  if (definition.pattern) {
    try {
      new RegExp(definition.pattern);
    } catch {
      errors.push(`${label} 的正则表达式无效`);
    }
  }
  return errors;
}

export function validateParameterDefinitions(definitions: ParameterDefinition[]): string[] {
  const errors: string[] = [];
  const names = new Set<string>();
  definitions.forEach((definition, index) => {
    const label = definition.label?.trim() || definition.name?.trim() || `参数 ${index + 1}`;
    if (!definition.name?.trim() || /[.\[\]]/.test(definition.name)) {
      errors.push(`${label} 的参数名必须是扁平 JSON 字段，不能包含 . [ ]`);
    } else if (names.has(definition.name)) {
      errors.push(`参数名 ${definition.name} 重复`);
    }
    names.add(definition.name);
    errors.push(...validateScalar(definition, label));
    if (definition.type === "array") {
      if (!definition.items) errors.push(`${label} 必须定义数组项类型`);
      else errors.push(...validateScalar(definition.items, `${label} 的数组项`, true));
      const min = definition.minItems || 0;
      const max = definition.maxItems || 20;
      if (max < 1 || max > 100) errors.push(`${label} 的最大项数必须在 1 到 100 之间`);
      if (min < 0 || min > max) errors.push(`${label} 的最小项数不能超过最大项数`);
      if (definition.defaultValue) {
        try {
          const parsed = JSON.parse(definition.defaultValue);
          if (!Array.isArray(parsed)) errors.push(`${label} 的默认值必须是 JSON 数组`);
        } catch {
          errors.push(`${label} 的默认值必须是合法 JSON 数组`);
        }
      }
    }
    if (definition.type === "json" && definition.defaultValue) {
      try {
        JSON.parse(definition.defaultValue);
      } catch {
        errors.push(`${label} 的默认值必须是合法 JSON`);
      }
    }
  });
  return errors;
}

export function validateResponseDefinition(definition: ResponseDisplayDefinition): string[] {
  const errors: string[] = [];
  if (!RESPONSE_MODES.has(definition.mode)) errors.push("返回展示模式无效");
  const validateFields = (fields: ResponseFieldDefinition[] | undefined, label: string) => {
    (fields || []).forEach((field, index) => {
      if (!field.path?.trim()) errors.push(`${label} ${index + 1} 缺少字段路径`);
      if (!RESPONSE_FORMATS.has(field.format)) errors.push(`${label} ${index + 1} 的格式无效`);
    });
  };
  validateFields(definition.fields, "展示字段");
  validateFields(definition.summaryFields, "汇总字段");
  if (["KEY_VALUE", "LIST", "TABLE"].includes(definition.mode) && !definition.fields?.length) {
    errors.push(`${definition.mode} 模式至少需要一个展示字段`);
  }
  if (definition.summaryFields?.length && definition.mode !== "LIST") {
    errors.push("只有 LIST 模式可以配置汇总字段");
  }
  if (definition.summaryFields?.length && !definition.root?.trim()) {
    errors.push("配置汇总字段时，LIST 模式必须填写数组数据根路径");
  }
  return errors;
}
